import {CommonModule} from '@angular/common';
import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ModuleWithProviders, NgModule, Optional, Provider, SkipSelf} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AlertModule} from 'ngx-bootstrap/alert';
import {CookieConsentBarComponent} from './components/cookie-consent-bar/cookie-consent-bar.component';
import {DATADOG_RUM_CONFIG, DD_BASE_API_URL} from './constants/datadog-init.constants';
import {WINDOW} from './constants/window.const';
import {IDatadogRumConfig} from './interfaces/datadog-config.interface';
import {DatadogRumLoggerService} from './services/datadog-rum-logger.service';
import {ConfigService} from './services/http-services/config.service';

export interface DatadogRumAsyncOptions {
  useFactory: (...deps: any[]) => IDatadogRumConfig;
  deps?: Provider[];
}

@NgModule({
  declarations: [CookieConsentBarComponent],
  exports: [CookieConsentBarComponent],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AlertModule.forRoot()],
  providers: [
    {
      provide: DD_BASE_API_URL,
      useFactory: (win: Window): string => win.location.origin.concat('/api'),
      deps: [WINDOW]
    },
    ConfigService,
    DatadogRumLoggerService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class JumioDatadogRumModule {
  constructor(@Optional() @SkipSelf() parentModule: JumioDatadogRumModule) {
    if (parentModule) {
      throw new Error('JumioDatadogRumModule has already been imported.');
    }
  }

  public static forRoot(config: IDatadogRumConfig): ModuleWithProviders<JumioDatadogRumModule> {
    return {
      ngModule: JumioDatadogRumModule,
      providers: [
        {provide: DATADOG_RUM_CONFIG, useValue: config},
        {
          provide: DatadogRumLoggerService,
          useFactory: (conf: IDatadogRumConfig, configService: ConfigService): DatadogRumLoggerService => {
            const serviceInstance = new DatadogRumLoggerService(conf, configService);
            serviceInstance.load();

            return serviceInstance;
          },
          deps: [DATADOG_RUM_CONFIG, ConfigService]
        }
      ]
    };
  }

  // Add a new forRootAsync that takes a factory function instead
  public static forRootAsync(options: DatadogRumAsyncOptions): ModuleWithProviders<JumioDatadogRumModule> {
    return {
      ngModule: JumioDatadogRumModule,
      providers: [
        // Provide config via a factory the user supplies
        {
          provide: DATADOG_RUM_CONFIG,
          useFactory: options.useFactory,
          deps: options.deps || []
        },
        {
          provide: DatadogRumLoggerService,
          useFactory: (conf: IDatadogRumConfig, configService: ConfigService): DatadogRumLoggerService => {
            const serviceInstance = new DatadogRumLoggerService(conf, configService);
            serviceInstance.load();
            return serviceInstance;
          },
          deps: [DATADOG_RUM_CONFIG, ConfigService]
        }
      ]
    };
  }
}
